/**
 * ListenerManager
 * 
 * Centralized service for managing real-time listeners.
 * Singleton pattern ensures single instance across application.
 * 
 * Features:
 * - Lifecycle management (init/cleanup)
 * - Error handling for all listeners
 * - Duplicate prevention
 * - Context-aware subscriptions
 * - Database adapter abstraction
 */

import { LISTENER_ADAPTER } from '../../config/database';
import logger from '../Logger';
import { 
  GLOBAL_ACTIONS, 
  CHAT_ACTIONS,
  CHAT_METADATA_ACTIONS, 
  CHAT_METADATA_HANDLER_KEYS,
  SPECTRA_ACTIONS, 
  SPECTRA_HANDLER_KEYS,
  LISTENER_CATEGORY 
} from './constants';
import { globalHandlers, chatMetadataHandlers, spectraHandlers, chatHandlers, isFreshAction, shouldProcessAction, CHAT_CORE_ACTIONS } from './handlers';

class ListenerManager {
  constructor() {
    if (ListenerManager.instance) {
      return ListenerManager.instance;
    }

    this.adapter = new LISTENER_ADAPTER();
    this.activeListeners = new Map(); // listenerId -> unsubscribe function
    this.handlers = new Map(); // action -> handler function
    this.context = null; // Stores user, navigate, stores, etc.
    this.initialized = false;
    // In-flight/completed initialize() promise. Lets whenInitialized() await
    // readiness directly instead of polling on a fixed timeout (see
    // whenInitialized() below for why that mattered).
    this._initPromise = null;
    this.dashboardRefetchCallback = null; // Callback for dashboard refetch on chat changes
    ListenerManager.instance = this;
  }

  /**
   * Initialize manager with user context and dependencies
   */
  async initialize(context) {
    if (this.initialized) {
      logger.warn('ListenerManager', 'Already initialized');
      return;
    }
    if (this._initPromise) {
      // Already in-flight (e.g. StrictMode double-invoke) — piggyback on the
      // existing call instead of racing a second one.
      return this._initPromise;
    }

    this._initPromise = (async () => {
      this.context = context;
      await this.adapter.initialize({
        orgId: context.orgId,
        userId: context.user?.uid,
      });
      this.registerGlobalHandlers();
      this.registerChatMetadataHandlers();
      this.registerSpectraHandlers();
      this.registerChatHandlers();
      this.initialized = true;
    })();

    return this._initPromise;
  }

  /**
   * Resolve once initialize() has completed.
   *
   * Used by hooks (useChatMetadataListeners, useDashboardListeners) that need
   * to subscribe as soon as the manager is ready but don't themselves call
   * initialize() — that's useListenerManager's job, in a different component.
   *
   * BUG FIX (2026-07-01): those hooks used to poll with a single fixed
   * setTimeout(..., 100) check and silently give up forever if initialize()
   * hadn't finished within exactly 100ms (slow network/cold start/main-thread
   * contention) — a real race with no retry. This removes the race entirely:
   * callers await the actual in-flight promise instead of guessing a delay.
   *
   * Falls back to a bounded poll only if initialize() hasn't been called yet
   * at all (defensive — in the normal app flow useListenerManager's effect
   * always runs first and sets _initPromise before these hooks' effects run).
   */
  whenInitialized() {
    if (this.initialized) return Promise.resolve();
    if (this._initPromise) return this._initPromise;

    return new Promise((resolve) => {
      const maxAttempts = 40; // 40 * 100ms = 4s safety net
      let attempts = 0;
      const check = () => {
        if (this.initialized || this._initPromise) {
          resolve(this._initPromise || Promise.resolve());
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          logger.error('ListenerManager', 'whenInitialized() timed out — initialize() was never called');
          resolve();
          return;
        }
        setTimeout(check, 100);
      };
      check();
    });
  }

  /**
   * Register global action handlers
   */
  registerGlobalHandlers() {
    Object.entries(globalHandlers).forEach(([action, handler]) => {
      this.handlers.set(action, handler);
    });
  }

  /**
   * Register chat metadata action handlers
   */
  registerChatMetadataHandlers() {
    Object.entries(chatMetadataHandlers).forEach(([action, handler]) => {
      this.handlers.set(action, handler);
    });
  }

  /**
   * Register spectra action handlers
   */
  registerSpectraHandlers() {
    Object.entries(spectraHandlers).forEach(([action, handler]) => {
      this.handlers.set(action, handler);
    });
  }

  /**
   * Register chat action handlers
   */
  registerChatHandlers() {
    Object.entries(chatHandlers).forEach(([action, handler]) => {
      this.handlers.set(action, handler);
    });
  }

  /**
   * Check if ListenerManager is initialized
   */
  isInitialized() {
    return this.initialized;
  }

  /**
   * Shared "not initialized" guard for all subscribe* entry points — logs and
   * returns false so the caller can bail out early. Extracted so the identical
   * check/log/return can't drift across the 4 subscribe* methods.
   */
  _requireInitialized() {
    if (!this.initialized) {
      logger.error('ListenerManager', 'Not initialized. Call initialize() first.');
      return false;
    }
    return true;
  }

  /**
   * Shared "missing user/orgId" guard, parameterized by a short label for the
   * warning message so each call site keeps a specific, actionable message.
  * Only used where user/orgId are the sole required fields.
   */
  _requireUserOrg(label) {
    const { user, orgId } = this.context || {};
    if (!user?.uid || !orgId) {
      logger.warn('ListenerManager', `Missing user or orgId for ${label}`);
      return null;
    }
    return { user, orgId };
  }

  /**
   * Subscribe to global listeners (active from login to logout)
   */
  subscribeGlobal() {
    if (!this._requireInitialized()) return;

    const userOrg = this._requireUserOrg('global listeners');
    if (!userOrg) return;
    const { user, orgId } = userOrg;

    const basePath = `organisation/${orgId}/users/${user.uid}/shared_actions`;

    Object.keys(globalHandlers).forEach((action) => {
      this.subscribe(basePath, action, LISTENER_CATEGORY.GLOBAL);
    });

  }

  /**
   * Generic subscribe method
   * @param {string} path - Collection path
   * @param {string} action - Document/action name (e.g., 'PIN_UNPIN_CHAT')
   * @param {string} category - Listener category for filtering
   * @param {string} handlerKey - Key to lookup handler (defaults to action)
   */
  subscribe(path, action, category, handlerKey = action) {
    const listenerId = `${path}/${action}`;

    // Check for duplicates (normal in React StrictMode)
    if (this.activeListeners.has(listenerId)) {
      return;
    }

    const handler = this.handlers.get(handlerKey);
    if (!handler) {
      logger.warn('ListenerManager', `No handler registered for key: ${handlerKey}`);
      return;
    }

    // Create listener via adapter
    const adapterId = this.adapter.createListener(
      path,
      action,
      (data) => this.handleData(data, handlerKey, category),
      (error) => this.handleError(error, listenerId)
    );

    this.activeListeners.set(listenerId, adapterId);
  }

  /**
   * Handle incoming data from listener
   */
  handleData(data, action, category) {
    if (!data) return;

    // Dashboard refetch should trigger for ALL events (including own actions)
    // since it's a data refresh, not a notification.
    // NOTE: `action` here is the HANDLER KEY (see subscribe()'s handlerKey param),
    // not the raw action name — chat-metadata and spectra EDIT/DELETE_CHAT events
    // are registered under METADATA_*/SPECTRA_* handler keys, never the bare
    // 'EDIT_CHAT'/'DELETE_CHAT' strings. Comparing against the raw strings here
    // silently never matched, so the dashboard never auto-refreshed.
    const isEditOrDeleteChat = (
      action === CHAT_METADATA_HANDLER_KEYS.EDIT_CHAT ||
      action === CHAT_METADATA_HANDLER_KEYS.DELETE_CHAT ||
      action === SPECTRA_HANDLER_KEYS.EDIT_CHAT ||
      action === SPECTRA_HANDLER_KEYS.DELETE_CHAT
    );
    if (this.dashboardRefetchCallback && isEditOrDeleteChat) {
      // Skip only stale actions (older than 5 seconds)
      const timestamp = data?.timestamp || data?.updated_at || data?.created_at;
      if (timestamp) {
        const actionTime = timestamp?.toDate?.() || new Date(timestamp);
        const now = new Date();
        const ageInSeconds = (now - actionTime) / 1000;
        if (ageInSeconds <= 5) {
          this.dashboardRefetchCallback();
        }
      }
    }

    // Skip actions triggered by current user or session (for toast notifications, etc.)
    const sessionId = this.context?.sessionId;
    const userId = this.context?.user?.uid;
    
    if (!shouldProcessAction(data, sessionId, userId)) {
      // Approval lifecycle events must refresh every open tab, including tabs
      // authenticated as the same user that initiated the action.
      const isApprovalLifecycleAction = (
        action === CHAT_ACTIONS.APPROVAL_CREATED ||
        action === CHAT_ACTIONS.APPROVAL_UPDATED
      );
      // Terminal generation events are emitted only after durable settlement.
      // Process fresh own-session events so the originating tab reconciles with
      // the backend after its inline generation stream has been aborted.
      const isGenerationTerminalAction = (
        action === CHAT_ACTIONS.GENERATION_COMPLETE ||
        action === CHAT_ACTIONS.GENERATION_STOPPED ||
        action === CHAT_ACTIONS.GENERATION_FAILED
      ) && isFreshAction(data);
      // Exception: own EDIT_CHAT events that carry a real title must still update the
      // React Query cache so the sidebar reflects the backend-generated title immediately.
      // emitAgain is false on these events so no toast fires.
      // Applies to both standalone (METADATA_EDIT_CHAT) and Spectra (SPECTRA_EDIT_CHAT) sparks.
      const isEditChatWithTitle = (
        (action === CHAT_METADATA_HANDLER_KEYS.EDIT_CHAT || action === SPECTRA_HANDLER_KEYS.EDIT_CHAT) &&
        data?.data?.title !== undefined
      );
      if (isEditChatWithTitle || isApprovalLifecycleAction || isGenerationTerminalAction) {
        const handler = this.handlers.get(action);
        if (handler) {
          handler(data, { ...this.context, listenerManager: this });
        }
      }
      return; // Skip own action for all other handlers (toasts, sidebar updates)
    }

    // Execute handler with extended context including listenerManager reference
    const handler = this.handlers.get(action);
    if (handler) {
      handler(data, { ...this.context, listenerManager: this });
    }
  }

  /**
   * Handle listener errors
   */
  handleError(error, listenerId) {
    logger.error('ListenerManager', `Error on ${listenerId}`, { error: error.message });
    
    // TODO: Add retry logic, cleanup after X failures, or alert user
  }

  /**
   * Unsubscribe from specific listener
   */
  unsubscribe(path, action) {
    const listenerId = `${path}/${action}`;
    const adapterId = this.activeListeners.get(listenerId);

    if (adapterId) {
      this.adapter.removeListener(adapterId);
      this.activeListeners.delete(listenerId);
    }
  }

  /**
   * Unsubscribe all global listeners
   */
  unsubscribeGlobal() {
    const { user, orgId } = this.context || {};
    if (!user?.uid || !orgId) return;

    const basePath = `organisation/${orgId}/users/${user.uid}/shared_actions`;

    Object.values(GLOBAL_ACTIONS).forEach((action) => {
      this.unsubscribe(basePath, action);
    });
  }

  /**
   * Subscribe to chat metadata listeners (active from login to logout)
   */
  subscribeChatMetadata() {
    if (!this._requireInitialized()) return;

    const userOrg = this._requireUserOrg('chat metadata listeners');
    if (!userOrg) return;
    const { user, orgId } = userOrg;

    const basePath = `organisation/${orgId}/users/${user.uid}/shared_actions_chats`;

    // Subscribe using action names but unique handler keys
    Object.entries(CHAT_METADATA_ACTIONS).forEach(([key, action]) => {
      const handlerKey = CHAT_METADATA_HANDLER_KEYS[key];
      this.subscribe(basePath, action, LISTENER_CATEGORY.CHAT_METADATA, handlerKey);
    });
  }

  /**
   * Unsubscribe all chat metadata listeners
   */
  unsubscribeChatMetadata() {
    const { user, orgId } = this.context || {};
    if (!user?.uid || !orgId) return;

    const basePath = `organisation/${orgId}/users/${user.uid}/shared_actions_chats`;

    Object.values(CHAT_METADATA_ACTIONS).forEach((action) => {
      this.unsubscribe(basePath, action);
    });
  }

  /**
   * Subscribe to spectra listeners for a specific spectra
   */
  subscribeSpectra(space) {
    if (!this._requireInitialized()) return;

    const { user, orgId } = this.context;
    if (!user?.uid || !orgId || !space) {
      logger.warn('ListenerManager', 'Missing user, orgId, or space for spectra listeners');
      return;
    }

    const basePath = `organisation/${orgId}/users/${space.owner_id}/spaces/${space.space_id}/shared_actions`;

    // Update context with current space
    this.updateContext({ space });

    // Subscribe using action names but unique handler keys
    Object.entries(SPECTRA_ACTIONS).forEach(([key, action]) => {
      const handlerKey = SPECTRA_HANDLER_KEYS[key];
      this.subscribe(basePath, action, LISTENER_CATEGORY.SPECTRA, handlerKey);
    });

  }

  /**
   * Unsubscribe from spectra listeners for a specific spectra
   */
  unsubscribeSpectra(space) {
    if (!space) return;

    const { user, orgId } = this.context || {};
    if (!user?.uid || !orgId) return;

    const basePath = `organisation/${orgId}/users/${space.owner_id}/spaces/${space.space_id}/shared_actions`;

    Object.values(SPECTRA_ACTIONS).forEach((action) => {
      this.unsubscribe(basePath, action);
    });

  }

  /**
   * Subscribe to chat listeners for a specific chat
   * 
   * Updates React Query cache when real-time events occur.
   */
  subscribeChat(chatId, space) {
    if (!this._requireInitialized()) return;

    const { user, orgId } = this.context;
    if (!user?.uid || !orgId || !space || !chatId) {
      logger.warn('ListenerManager', 'Missing user, orgId, space, or chatId for chat listeners');
      return;
    }

    const basePath = `organisation/${orgId}/users/${space.owner_id}/chats/${chatId}/shared_actions_chats`;

    // Update context with current chat
    this.updateContext({ chatId, space });

    CHAT_CORE_ACTIONS.forEach((action) => {
      this.subscribe(basePath, action, LISTENER_CATEGORY.CHAT);
    });
  }

  /**
   * Unsubscribe from chat listeners for a specific chat
   */
  unsubscribeChat(chatId, space) {
    if (!chatId || !space) return;

    const { user, orgId } = this.context || {};
    if (!user?.uid || !orgId) return;

    const basePath = `organisation/${orgId}/users/${space.owner_id}/chats/${chatId}/shared_actions_chats`;

    CHAT_CORE_ACTIONS.forEach((action) => {
      this.unsubscribe(basePath, action);
    });
  }

  // ========================================
  // DASHBOARD LISTENERS (SparksDash page)
  // ========================================

  /**
   * Register a dashboard refetch callback.
   * The existing chat metadata handlers (EDIT_CHAT, DELETE_CHAT) will call this
   * callback when events are received, triggering a silent refetch of the chat list.
   * 
   * This approach reuses the existing listeners from subscribeChatMetadata() rather than
   * creating duplicate listeners.
   * 
   * @param {Function} refetchCallback - Callback to trigger chat list refetch
   */
  subscribeDashboard(refetchCallback) {
    if (!this._requireInitialized()) return;

    if (typeof refetchCallback !== 'function') {
      logger.error('ListenerManager', 'subscribeDashboard requires a refetchCallback function');
      return;
    }

    this.dashboardRefetchCallback = refetchCallback;
    this._dashboardSubscribed = true;
  }

  /**
   * Unregister the dashboard refetch callback.
   */
  unsubscribeDashboard() {
    if (!this._dashboardSubscribed) return;

    this.dashboardRefetchCallback = null;
    this._dashboardSubscribed = false;
  }

  /**
   * Get the dashboard refetch callback (used by handlers)
   */
  getDashboardRefetchCallback() {
    return this.dashboardRefetchCallback;
  }

  /**
   * Cleanup all listeners and reset manager
   */
  async cleanup() {
    await this.adapter.cleanup();
    this.activeListeners.clear();
    this.handlers.clear();
    this.context = null;
    this.initialized = false;
    this._initPromise = null;
    this.dashboardRefetchCallback = null;
  }

  /**
   * Get count of active listeners
   */
  getActiveListenerCount() {
    return this.activeListeners.size;
  }

  /**
   * Update context (useful for navigation changes)
   */
  updateContext(newContext) {
    this.context = { ...this.context, ...newContext };
  }
}

// Export singleton instance
const listenerManager = new ListenerManager();
export default listenerManager;
